/**
 * Export deliveries to CSV based on currently displayed data.
 * Each item in the items JSONB array becomes a separate row in the CSV.
 */

const CSV_HEADERS = [
  'Name',
  'Suburb',
  'State',
  'Post Code',
  'Street',
  'Street No',
  'Unit No',
  'Mobile',
  'Email',
  'DOC_TYPE',
  'Customer No',
  'Customer Reference No',
  'SKU',
  'Facility',
  'Qty',
  'REQ_DATE_H',
  'Delivery Type',
  'Installation',
  'SERIAL_NO',
  'Return Reason',
  'Delivery Notes',
  'Direction notes',
  'SAP SO Number',
  'Pickup SAP SO Number',
];

/**
 * Escape a value for CSV output.
 * Wraps in double quotes if the value contains commas, quotes, or newlines.
 */
function escapeCsvValue(value) {
  if (value === null || value === undefined) {
    return '';
  }

  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Extract the actual SKU from the prefixed barcode stored on item.sku.
 *
 * Storis barcode format: `{lineNumber}-{orderId}-{SKU}[-{subIndex}]`
 *   - {SKU} can itself contain hyphens, e.g. "CR11-H851-0023469"
 *   - {subIndex} is a 1-3 digit integer present only when quantity > 1
 *
 * Examples:
 *   "1-101395209-CR11-H851-0023469-1" -> "CR11-H851-0023469"
 *   "1-101395209-CR11-H851-0023469"   -> "CR11-H851-0023469"
 *   "5-ORD123-SOFA-2"                 -> "SOFA"
 *   "SOFA"                             -> "SOFA"  (no prefix to strip)
 */
function cleanSku(rawSku) {
  if (rawSku == null) return '';
  const parts = String(rawSku).split('-');

  // Fewer than three segments => doesn't match the barcode shape; pass through.
  if (parts.length < 3) return String(rawSku);

  // Drop the two prefix segments (lineNumber, orderId).
  let skuParts = parts.slice(2);

  // Drop the trailing sub-index (small 1-3 digit integer) ONLY when at least
  // one segment of real SKU remains in front of it — otherwise we'd be
  // stripping the SKU itself for SKUs that happen to be a short integer.
  if (skuParts.length > 1 && /^\d{1,3}$/.test(skuParts[skuParts.length - 1])) {
    skuParts = skuParts.slice(0, -1);
  }

  return skuParts.join('-');
}

/**
 * Convert a delivery + item to a CSV row array.
 */
function deliveryItemToRow(delivery, item) {
  return [
    delivery.name,
    delivery.suburb,
    delivery.state,
    delivery.postCode,
    delivery.street,
    delivery.streetNo,
    delivery.unitNo,
    delivery.mobile,
    delivery.email,
    delivery.docType,
    delivery.customerNo,
    delivery.customerReference,
    cleanSku(item?.sku),
    delivery.facility,
    item?.qty,
    delivery.reqDate,
    delivery.deliveryType,
    delivery.installation,
    item?.serial_no,
    item?.return_reason,
    delivery.deliveryNotes,
    delivery.directionNotes,
    delivery.sapSoNumber,
    delivery.pickupSapSoNumber,
  ];
}

/**
 * Convert deliveries to CSV string.
 * Flattens items JSONB so each item is its own row.
 */
export function deliveriesToCsv(deliveries) {
  const rows = [];

  for (const delivery of deliveries) {
    const items = delivery.items || [];
    if (items.length > 0) {
      for (const item of items) {
        rows.push(deliveryItemToRow(delivery, item));
      }
    } else {
      rows.push(deliveryItemToRow(delivery, null));
    }
  }

  const headerLine = CSV_HEADERS.map(escapeCsvValue).join(',');
  const dataLines = rows.map((row) => row.map(escapeCsvValue).join(','));

  return [headerLine, ...dataLines].join('\r\n');
}

/**
 * Trigger a CSV file download in the browser.
 */
export function downloadCsv(csvContent, filename = 'orders-export.csv') {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export currently displayed deliveries as a CSV download.
 * @param {Array} deliveries - the rows to export
 * @param {string} [tabName] - optional operation/tab name appended to filename
 */
export function exportOrdersCsv(deliveries, tabName) {
  const csv = deliveriesToCsv(deliveries);
  const timestamp = new Date().toISOString().slice(0, 10);
  const safeTab = tabName ? `-${String(tabName).replace(/[^A-Za-z0-9_-]/g, '')}` : '';
  downloadCsv(csv, `orders-export${safeTab}-${timestamp}.csv`);
}
